import { Link, createFileRoute } from '@tanstack/react-router'
import { useState } from 'react'
import { useGenerationTypes } from '@/features/docs/hooks/useGenerationsDocs'
import { getAdjacentItems } from '@/features/docs/config/navigation'
import { DocsBreadcrumb } from '@/features/docs/components/DocsBreadcrumb'
import { DocsCopyButton } from '@/features/docs/components/DocsCopyButton'
import { generateLinks, generateMetaTags } from '@/lib/seo'
import { getBreadcrumbSchema, renderJsonLd } from '@/lib/structured-data'

const SLUG = 'api-reference/generation-types'
const TITLE = 'Generation Types'

export const Route = createFileRoute('/docs/api-reference/generation-types')({
  head: () => ({
    meta: generateMetaTags({
      title: `${TITLE} - ModelStack Docs`,
      description:
        'Explore every generation type supported by the ModelStack Generations API and which models support each one.',
      canonical: `/docs/${SLUG}`,
    }),
    links: generateLinks({ canonical: `/docs/${SLUG}` }),
    scripts: [
      {
        type: 'application/ld+json',
        children: renderJsonLd(
          getBreadcrumbSchema([
            { name: 'Home', url: '/' },
            { name: 'Docs', url: '/docs' },
            { name: TITLE, url: `/docs/${SLUG}` },
          ]) as any,
        ),
      },
    ],
  }),
  component: GenerationTypesPage,
})

function GenerationTypesPage() {
  const { data, loading, error } = useGenerationTypes()
  const [expandedType, setExpandedType] = useState<string | null>(null)
  const { prev, next } = getAdjacentItems(SLUG)

  return (
    <div className="flex gap-0">
      <div className="flex-1 min-w-0 px-8 py-10 max-w-3xl">
        <DocsBreadcrumb slug={SLUG} />

        <div className="flex items-start justify-between gap-4 mb-2">
          <h1 className="text-3xl font-bold text-[var(--color-text-primary)] tracking-[-0.03em] md:text-4xl">
            Generation Types
          </h1>
          <DocsCopyButton slug={SLUG} />
        </div>
        <p className="text-[var(--color-text-secondary)] text-lg mb-8">
          Explore all {data?.totalTypes ?? '...'} available generation types and their capabilities
        </p>

        {loading && (
          <div className="space-y-6">
            {[1, 2, 3].map((i) => (
              <div key={i} className="space-y-3">
                <div className="h-8 bg-[var(--color-bg-tertiary)] rounded-full w-48 animate-pulse" />
                <div className="border border-[var(--color-border)] rounded-2xl p-6 animate-pulse">
                  <div className="h-6 bg-[var(--color-bg-tertiary)] rounded-full w-64" />
                </div>
              </div>
            ))}
          </div>
        )}

        {error && (
          <div className="text-center py-12 text-[var(--color-error)]">
            <p>Failed to load generation types: {error}</p>
          </div>
        )}

        {!loading && !error && data?.categories.map((category) => (
          <section key={category.category} id={`category-${category.category}`} className="mb-10 scroll-mt-20">
            <h2 className="text-xl font-bold text-[var(--color-text-primary)] mb-4 capitalize">{category.category} Generation</h2>

            <div className="space-y-3">
              {category.types.map((type) => (
                <TypeCard
                  key={type.type}
                  type={type}
                  isExpanded={expandedType === type.type}
                  onToggle={() => setExpandedType(expandedType === type.type ? null : type.type)}
                />
              ))}
            </div>
          </section>
        ))}

        {/* Prev/Next navigation */}
        {(prev || next) && (
          <div className="flex items-center justify-between gap-4 mt-12 pt-8 border-t border-[var(--color-border)]">
            {prev ? (
              <Link
                // @ts-expect-error - Dynamic doc routes not in generated types
                to={`/docs/${prev.slug}`}
                className="flex items-center gap-3 px-4 py-3 rounded-2xl border border-[var(--color-border)] hover:border-[var(--color-primary)]/40 hover:bg-[var(--color-bg-secondary)]/70 hover:backdrop-blur-md hover:-translate-y-0.5 hover:shadow-[0_18px_40px_-24px_var(--color-shadow)] transition-all duration-300 ease-brisk group max-w-[45%]"
              >
                <span className="material-symbols-outlined text-lg text-[var(--color-text-tertiary)] group-hover:text-[var(--color-primary)] shrink-0">
                  arrow_back
                </span>
                <div className="text-left min-w-0">
                  <div className="text-xs text-[var(--color-text-tertiary)] mb-0.5">Previous</div>
                  <div className="text-sm font-medium text-[var(--color-text-primary)] group-hover:text-[var(--color-primary)] truncate">
                    {prev.title}
                  </div>
                </div>
              </Link>
            ) : (
              <div />
            )}
            {next && (
              <Link
                // @ts-expect-error - Dynamic doc routes not in generated types
                to={`/docs/${next.slug}`}
                className="flex items-center gap-3 px-4 py-3 rounded-2xl border border-[var(--color-border)] hover:border-[var(--color-primary)]/40 hover:bg-[var(--color-bg-secondary)]/70 hover:backdrop-blur-md hover:-translate-y-0.5 hover:shadow-[0_18px_40px_-24px_var(--color-shadow)] transition-all duration-300 ease-brisk group ml-auto max-w-[45%]"
              >
                <div className="text-right min-w-0">
                  <div className="text-xs text-[var(--color-text-tertiary)] mb-0.5">Next</div>
                  <div className="text-sm font-medium text-[var(--color-text-primary)] group-hover:text-[var(--color-primary)] truncate">
                    {next.title}
                  </div>
                </div>
                <span className="material-symbols-outlined text-lg text-[var(--color-text-tertiary)] group-hover:text-[var(--color-primary)] shrink-0">
                  arrow_forward
                </span>
              </Link>
            )}
          </div>
        )}
      </div>

      {/* Right ToC rail */}
      <div className="hidden xl:block w-56 shrink-0 px-4 py-10">
        <nav className="sticky top-24 space-y-3">
          <p className="px-3 text-xs font-semibold uppercase tracking-widest text-[var(--color-text-tertiary)]">
            On this page
          </p>
          <ul className="space-y-0.5 text-sm">
            {data?.categories.map((cat) => (
              <li key={cat.category}>
                <a
                  href={`#category-${cat.category}`}
                  className="block rounded-full px-3 py-1.5 capitalize text-[var(--color-text-secondary)] transition-all duration-300 ease-brisk hover:bg-[var(--color-control-surface-hover)] hover:text-[var(--color-text-primary)]"
                >
                  {cat.category} Generation
                </a>
              </li>
            ))}
          </ul>
        </nav>
      </div>
    </div>
  )
}

function TypeCard({
  type,
  isExpanded,
  onToggle,
}: {
  type: NonNullable<ReturnType<typeof useGenerationTypes>['data']>['categories'][0]['types'][0]
  isExpanded: boolean
  onToggle: () => void
}) {
  return (
    <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-bg-secondary)]/70 backdrop-blur-md overflow-hidden transition-colors duration-300 ease-brisk hover:border-[var(--color-primary)]/40">
      <button
        onClick={onToggle}
        className="w-full px-6 py-4 flex items-start justify-between text-left transition-colors duration-300 ease-brisk hover:bg-[var(--color-bg-tertiary)]"
      >
        <div className="text-left">
          <h3 className="text-lg font-semibold font-mono">{type.type}</h3>
          <p className="text-sm text-[var(--color-text-secondary)] mt-1">{type.label}</p>
        </div>
        <span className={`text-2xl text-[var(--color-text-secondary)] transition-transform duration-300 ease-brisk ${isExpanded ? 'rotate-180' : ''}`}>
          ▼
        </span>
      </button>

      {isExpanded && (
        <div className="bg-[var(--color-bg-secondary)] px-6 py-4 space-y-4 border-t border-[var(--color-border)]">
          <div>
            <h4 className="font-semibold mb-2">Supported Models</h4>
            <div className="flex flex-wrap gap-2">
              {type.families.map((family) => (
                <span
                  key={family}
                  className="text-xs px-2 py-1 rounded-full border border-[var(--color-border)] capitalize text-[var(--color-text-secondary)]"
                >
                  {family}
                </span>
              ))}
            </div>
            <p className="text-xs text-[var(--color-text-secondary)] mt-2">
              {type.modelCount} model{type.modelCount !== 1 ? 's' : ''} support this generation type
            </p>
          </div>

          <div>
            <h4 className="font-semibold mb-2">Available Parameters</h4>
            <div className="flex flex-wrap gap-2">
              {type.params.map((param) => (
                <code
                  key={param}
                  className="text-xs px-2.5 py-1 rounded-full bg-[var(--color-bg-tertiary)] text-[var(--color-text-secondary)]"
                >
                  {param}
                </code>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
